<template>
  <div class="settings-static" :data-testid="`settings-static-${page}`">
    <!-- Last updated badge -->
    <p v-if="lastUpdated" class="ssc-updated">{{ lastUpdated }}</p>

    <!-- About page (structured content) -->
    <div v-if="page === 'about'" class="ssc-card">
      <h3>{{ STRINGS.legal.pages.about.whatIs.heading }}</h3>
      <p>{{ STRINGS.legal.pages.about.whatIs.body }}</p>

      <hr class="ssc-divider" />

      <h3>{{ STRINGS.legal.pages.about.attributions.heading }}</h3>
      <p>{{ STRINGS.legal.pages.about.attributions.body }}</p>
      <ul>
        <li>
          <a href="https://en.wiktionary.org/" target="_blank" rel="noopener noreferrer"
            >Wiktionary.org</a
          >
        </li>
        <li>
          <a href="https://kaikki.org/" target="_blank" rel="noopener noreferrer">kaikki.org</a>
        </li>
        <li>
          <a
            href="https://github.com/tatuylonen/wiktextract"
            target="_blank"
            rel="noopener noreferrer"
            >wiktextract</a
          >
          by Tatu Ylonen
        </li>
      </ul>
      <p>{{ STRINGS.legal.pages.about.attributions.datasets }}</p>
      <ul>
        <li>
          <a
            href="https://github.com/borb-dev/public-assets"
            target="_blank"
            rel="noopener noreferrer"
            >borb-dev/public-assets</a
          >
        </li>
      </ul>

      <hr class="ssc-divider" />

      <h3>{{ STRINGS.legal.pages.about.contact.heading }}</h3>
      <p>{{ STRINGS.legal.pages.about.contact.body }}</p>

      <hr class="ssc-divider" />

      <p class="ssc-copyright">{{ STRINGS.legal.pages.about.copyright }}</p>
    </div>

    <!-- Cookies (structured content from STRINGS) -->
    <div v-else-if="page === 'cookies'" class="ssc-card">
      <h3>{{ STRINGS.legal.pages.cookies.whatAreCookies.heading }}</h3>
      <p>{{ STRINGS.legal.pages.cookies.whatAreCookies.body }}</p>

      <h3>{{ STRINGS.legal.pages.cookies.essentialCookies.heading }}</h3>
      <p>{{ STRINGS.legal.pages.cookies.essentialCookies.body }}</p>
      <ul>
        <li v-for="item in STRINGS.legal.pages.cookies.essentialCookies.items" :key="item">
          {{ item }}
        </li>
      </ul>

      <h3>{{ STRINGS.legal.pages.cookies.localStorage.heading }}</h3>
      <p>{{ STRINGS.legal.pages.cookies.localStorage.body }}</p>
      <ul>
        <li v-for="item in STRINGS.legal.pages.cookies.localStorage.items" :key="item">
          {{ item }}
        </li>
      </ul>

      <h3>{{ STRINGS.legal.pages.cookies.analytics.heading }}</h3>
      <p>{{ STRINGS.legal.pages.cookies.analytics.body }}</p>

      <hr class="ssc-divider" />

      <h3>{{ STRINGS.legal.pages.cookies.managingCookies.heading }}</h3>
      <p>{{ STRINGS.legal.pages.cookies.managingCookies.body }}</p>

      <h3>{{ STRINGS.legal.pages.cookies.contact.heading }}</h3>
      <p>{{ STRINGS.legal.pages.cookies.contact.body }}</p>
    </div>

    <!-- Privacy / Terms (HTML content) -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-else class="ssc-legal" v-html="legalHtml"></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import STRINGS from '@/constants/strings';
import privacyHtml from '@/content/privacy-policy.html?raw';
import termsHtml from '@/content/terms-of-use.html?raw';

export type StaticPage = 'about' | 'privacy' | 'terms' | 'cookies';

const props = defineProps<{
  page: StaticPage;
}>();

const lastUpdated = computed(() => {
  if (props.page === 'privacy') return STRINGS.legal.pages.privacy.lastUpdated;
  if (props.page === 'terms') return STRINGS.legal.pages.terms.lastUpdated;
  if (props.page === 'cookies') return STRINGS.legal.pages.cookies.lastUpdated;
  return null;
});

const legalHtml = computed(() => {
  if (props.page === 'privacy') return privacyHtml;
  if (props.page === 'terms') return termsHtml;
  return '';
});
</script>

<style scoped>
.settings-static {
  display: flex;
  flex-direction: column;
}

.ssc-updated {
  font-size: 0.75rem;
  color: var(--color-arena-text-muted);
  margin: 0 0 1rem;
}

/* ── About / Cookies card ── */
.ssc-card h3 {
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  margin: 1.5rem 0 0.5rem 0;
  letter-spacing: 0.04em;
}

.ssc-card h3:first-child {
  margin-top: 0;
}

.ssc-card p {
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  line-height: 1.7;
  color: var(--color-arena-text-muted);
  margin: 0 0 0.75rem 0;
}

.ssc-card p:last-child {
  margin-bottom: 0;
}

.ssc-card ul {
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  line-height: 1.7;
  color: var(--color-arena-text-muted);
  margin: 0 0 0.75rem 0;
  padding-left: 1.25rem;
}

.ssc-card li {
  margin-bottom: 0.25rem;
}

.ssc-card a {
  color: var(--color-energy-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.ssc-card a:hover {
  color: var(--color-primary-600);
}

.ssc-divider {
  border: none;
  border-top: 1px solid var(--color-arena-border);
  margin: 1.5rem 0;
}

.ssc-copyright {
  font-family: var(--font-sans);
  font-size: 0.75rem;
  color: var(--color-arena-text-dim);
  text-align: center;
  margin: 0;
}

/* ── Legal content (privacy + terms) ── */

/* TOC */
.ssc-legal :deep(.toc) {
  background: var(--color-arena-raised);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-sm);
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
}

.ssc-legal :deep(.toc-label) {
  font-family: var(--font-display);
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--color-arena-text-muted);
  margin-bottom: 0.5rem;
}

.ssc-legal :deep(.toc ol) {
  list-style: decimal;
  padding-left: 1.25rem;
  font-size: 0.75rem;
  line-height: 1.9;
  color: var(--color-energy-primary);
}

.ssc-legal :deep(.toc a) {
  color: var(--color-energy-primary);
  text-decoration: none;
}

.ssc-legal :deep(.toc a:hover) {
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* Cards */
.ssc-legal :deep(.card) {
  background: var(--color-arena-raised);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-sm);
  padding: 1.25rem;
  margin-bottom: 0.75rem;
}

.ssc-legal :deep(.card h2) {
  font-family: var(--font-display);
  font-size: 0.6875rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-energy-primary);
  margin: 1.5rem 0 0.75rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-arena-border);
}

.ssc-legal :deep(.card h2:first-child) {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.ssc-legal :deep(.card h3) {
  font-family: var(--font-display);
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  margin: 1.25rem 0 0.375rem;
  letter-spacing: 0.02em;
}

.ssc-legal :deep(.card p) {
  font-size: 0.8125rem;
  line-height: 1.7;
  color: var(--color-arena-text-muted);
  margin-bottom: 0.75rem;
}

.ssc-legal :deep(.card p:last-child) {
  margin-bottom: 0;
}

.ssc-legal :deep(.card ul),
.ssc-legal :deep(.card ol) {
  font-size: 0.8125rem;
  line-height: 1.7;
  color: var(--color-arena-text-muted);
  padding-left: 1.25rem;
  margin-bottom: 0.75rem;
}

.ssc-legal :deep(.card li) {
  margin-bottom: 0.2rem;
}

.ssc-legal :deep(.card a) {
  color: var(--color-energy-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.ssc-legal :deep(.card a:hover) {
  color: var(--color-primary-600);
}

.ssc-legal :deep(.card strong) {
  color: var(--color-arena-text-bright);
  font-weight: 600;
}

.ssc-legal :deep(.card em) {
  font-style: italic;
}

/* Summary callout */
.ssc-legal :deep(.summary) {
  background: rgba(201, 168, 76, 0.06);
  border-left: 2px solid var(--color-energy-primary);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  padding: 0.625rem 0.875rem;
  margin: 0 0 1rem;
}

.ssc-legal :deep(.summary p) {
  font-size: 0.75rem;
  font-style: italic;
  margin-bottom: 0;
  color: var(--color-arena-text-muted);
}

/* Notice callout */
.ssc-legal :deep(.notice) {
  border: 1px solid rgba(91, 127, 164, 0.2);
  border-radius: var(--radius-sm);
  padding: 0.625rem 0.875rem;
  margin: 0.75rem 0;
  font-size: 0.75rem;
  color: var(--color-arena-text-muted);
  line-height: 1.7;
}

/* Tables */
.ssc-legal :deep(.tbl-wrap) {
  overflow-x: auto;
  margin: 0.75rem 0 1rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-arena-border);
}

.ssc-legal :deep(table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.ssc-legal :deep(thead th) {
  background: var(--color-arena-surface);
  color: var(--color-arena-text-bright);
  font-family: var(--font-display);
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.5rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--color-arena-border);
  white-space: nowrap;
}

.ssc-legal :deep(tbody td) {
  color: var(--color-arena-text-muted);
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid var(--color-arena-border);
  vertical-align: top;
  line-height: 1.6;
}

.ssc-legal :deep(tbody tr:last-child td) {
  border-bottom: none;
}

.ssc-legal :deep(tbody tr:nth-child(even) td) {
  background: rgba(255, 255, 255, 0.015);
}

/* Badges */
.ssc-legal :deep(.yes) {
  display: inline-block;
  background: rgba(0, 196, 143, 0.12);
  color: var(--color-energy-success);
  border: 1px solid rgba(0, 196, 143, 0.25);
  border-radius: 0.25rem;
  padding: 0.1rem 0.4rem;
  font-family: var(--font-mono);
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ssc-legal :deep(.no) {
  display: inline-block;
  color: var(--color-arena-text-dim);
  border: 1px solid var(--color-arena-border);
  border-radius: 0.25rem;
  padding: 0.1rem 0.4rem;
  font-family: var(--font-mono);
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

/* Contact block */
.ssc-legal :deep(.contact) {
  background: var(--color-arena-surface);
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-sm);
  padding: 0.75rem 1rem;
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
  line-height: 1.9;
}

/* Code */
.ssc-legal :deep(code) {
  font-family: var(--font-mono);
  font-size: 0.75em;
  background: var(--color-arena-surface);
  padding: 0.1em 0.3em;
  border-radius: 0.2rem;
  color: var(--color-arena-text-bright);
}

/* Focus styles */
.ssc-legal :deep(a:focus-visible) {
  outline: 2px solid var(--color-energy-primary);
  outline-offset: 3px;
  border-radius: 2px;
}
</style>
